import React, { useContext } from 'react';
import { AnimatePresence } from 'framer-motion';
import { orderBy } from 'lodash';

import MotionSection from '@shared/components/animations/MotionSection';
import EventAssignmentsTable from './EventAssignmentsTable';

import { getEventTeamRoleAssignmentSettings } from '../../../services/eventAssignmentTableService';
import { TEAM_TABLE_TYPE } from '../../../constants/AppraiserAssignmentTeamTableType';
import { APPRAISER_ASSIGNMENT_ATM_TYPES } from '../../../constants/AppraiserAssignmentTypes';
import { EVENT_APPRAISER_ASSIGNMENT_ROLE } from '../../../constants/EventAppraiserAssignmentRole';
import { ROLE_FIELD } from './constants/EventAssignmentsTableConstants';
import { EventAssignmentLoadingContext } from '../../shared/contexts/EventAssignmentLoadingContext';
import { EVENT_ASSIGNMENTS_TABLE_MODE } from './constants/EventAssignmentsTableMode';
import { removeUserObservationAssignments } from '../../shared/utils/observationAssignmentsUtils';

const ASSIGNMENT_TABLE_MAP = {
  [TEAM_TABLE_TYPE.CORE]: [
    { assignmentType: APPRAISER_ASSIGNMENT_ATM_TYPES.ISACA_ATMS, title: 'ISACA ATMs' },
    { assignmentType: APPRAISER_ASSIGNMENT_ATM_TYPES.INTERNAL_ATMS, title: 'Internal ATMs' }
  ],
  [TEAM_TABLE_TYPE.EXTRA]: [
    { assignmentType: APPRAISER_ASSIGNMENT_ATM_TYPES.ISACA_EXTRAS, title: 'ISACA Extras' },
    { assignmentType: APPRAISER_ASSIGNMENT_ATM_TYPES.INTERNAL_EXTRAS, title: 'Internal Extras' },
    { assignmentType: APPRAISER_ASSIGNMENT_ATM_TYPES.FDA_EXTRAS, title: 'FDA Extras' }
  ]
};

/**
 * @typedef {object} EventAssignmentTableWrapperProps
 * @property {string} [tableType]
 * @property {Array<*>} [eventTeams]
 * @property {Array<*>} [eventAssignments]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} [setEventFormData]
 * @property {boolean} [showUnavailableAtms]
 * @property {boolean} [showAssignmentRank]
 * @property {boolean} [canAssignATMs]
 */

/**
 * @param {EventAssignmentTableWrapperProps} props
 */
const EventAssignmentTableWrapper = props => {
  const {
    tableType,
    eventTeams = [],
    eventAssignments = [],
    setEventFormData,
    showUnavailableAtms,
    showAssignmentRank = false,
    canAssignATMs = false
  } = props;
  const teamRoleAssignmentSettings = getEventTeamRoleAssignmentSettings(
    eventTeams,
    eventAssignments
  );

  const assignmentsLoading = useContext(EventAssignmentLoadingContext);

  const getEventAssignmentsByType = (type, isOrderbyRole = false) => {
    const filteredAssignments = eventAssignments.filter(assignment => assignment.type === type);

    const sortByName = user => user.name.toLowerCase();
    const sortByRole = user => user.currentRoleName;

    return orderBy(
      filteredAssignments,
      isOrderbyRole ? [sortByRole, sortByName] : [sortByName],
      isOrderbyRole ? ['desc', 'asc'] : undefined
    );
  };

  const setATMValue = (externalUserId, value, field) => {
    setEventFormData(prevFormData => ({
      ...prevFormData,
      eventAssignments: updateAssignments(
        prevFormData.eventAssignments,
        externalUserId,
        value,
        field
      ),
      atmObservationAssignments: isRemovedFromPrimary(field, value)
        ? removeUserObservationAssignments(prevFormData.atmObservationAssignments, externalUserId)
        : prevFormData.atmObservationAssignments
    }));
  };

  const assignmentSettingsProvider = teamRole => {
    const roleSettings = teamRoleAssignmentSettings.find(setting => setting.role === teamRole);

    return {
      isAssignmentFilled: roleSettings.isAssignmentFilled,
      takenRankings: roleSettings.takenRankings
    };
  };

  return (
    <div className='tables-container'>
      <AnimatePresence mode='sync'>
        {ASSIGNMENT_TABLE_MAP[tableType].map(({ assignmentType, title }) => {
          const tableRoleAssignments = teamRoleAssignmentSettings.filter(setting =>
            setting.assignmentTypes.includes(assignmentType)
          );

          const shouldShowTable = tableRoleAssignments.some(
            setting => setting.hasPositiveNumberOfPeople
          );

          if (!shouldShowTable) return null;

          const key = `${tableType}-${assignmentType}`;

          return (
            <MotionSection keyProp={key} key={key}>
              <h4 className='section-title bold'>Select {title}</h4>
              <EventAssignmentsTable
                data={getEventAssignmentsByType(assignmentType)}
                mode={EVENT_ASSIGNMENTS_TABLE_MODE.TEAM_TABLE}
                assignmentSettingsProvider={assignmentSettingsProvider}
                setATMValue={setATMValue}
                canEditRanking
                showUnavailableAtms={showUnavailableAtms}
                showAssignmentRank={showAssignmentRank}
                canAssignATMs={canAssignATMs}
                loading={assignmentsLoading}
              />
            </MotionSection>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

const isRemovedFromPrimary = (field, value) =>
  field === ROLE_FIELD && value !== EVENT_APPRAISER_ASSIGNMENT_ROLE.PRIMARY;

const updateAssignments = (eventAssignments, externalUserId, value, field) =>
  eventAssignments.map(assignment => {
    if (assignment.externalUserId === externalUserId) {
      const updatedAssignment = { ...assignment, [field]: value };

      if (isRemovedFromPrimary(field, value)) {
        updatedAssignment.canBeObserved = false;
        updatedAssignment.canObserve = false;
      }

      return updatedAssignment;
    }

    return assignment;
  });

export default EventAssignmentTableWrapper;
